import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import CopyMealToDateDialog from './CopyMealToDateDialog'
import { MEAL_CONFIG } from './mealConfig'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { nutritionAPI } from '../../services/api'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  PencilIcon,
  PlusIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  SparklesIcon,
  StarIcon,
  CalendarIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'
import SaveAsFavoriteModal from './SaveAsFavoriteModal'
import MacroBadge from '../shared/MacroBadge'
import NutritionFoodCard from './MealTimelineCard/NutritionFoodCard'
import { contentLanguage } from '../../i18n/supported'

// VH-123: The food-photo intake handler writes this fixed provenance string
// into meals.description when the user did NOT supply a preparation note. It is
// an internal placeholder, not user content, so it must never surface in the UI
// as if it were a note. Anything else in `description` is the user's own note.
const GENERIC_INTAKE_DESCRIPTION = 'Meal created from email photo intake'

/**
 * MealTimelineCard Component - Compact Design with Inline Editing + AI Food Addition
 *
 * Collapsed: Single line showing meal type, time, food names, calories, edit/delete buttons
 * Expanded: Inline edit mode for editing food items, quantities, and nutritional values
 * Add Food: AI-assisted food description analysis for automatic nutrition lookup
 */

export default function MealTimelineCard({
  mealId,
  mealType = 'BREAKFAST',
  mealTime,
  logDate = null,
  photo: _photo = null,
  note = null,
  foods = [],
  totals = {
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  },
  isExpanded = false,
  onToggle = () => {},
  onEdit: _onEdit = () => {},
  onDelete = () => {},
  onAddPhoto: _onAddPhoto = () => {},
  onMealUpdated = () => {},
  // VH-161: when provided, the card offers 'copy this meal to another day'.
  // Omitted on surfaces where copying makes no sense, so the button simply
  // does not render rather than appearing and failing.
  onCopyMeal = null
}) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  // Normalize mealType to uppercase (API returns lowercase, internal logic uses uppercase)
  const normalizedMealType = mealType?.toUpperCase?.() || mealType

  // Today as YYYY-MM-DD in the user's local timezone (used as the picker's `max`)
  const todayIso = (() => {
    const now = new Date()
    const y = now.getFullYear()
    const m = String(now.getMonth() + 1).padStart(2, '0')
    const d = String(now.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  })()

  // Edit mode state
  const [isInEditMode, setIsInEditMode] = useState(false)
  const [editingFoods, setEditingFoods] = useState([])
  const [editingMealType, setEditingMealType] = useState(normalizedMealType)
  const [editingLogDate, setEditingLogDate] = useState((logDate || todayIso).slice(0, 10))
  const [isSavingDate, setIsSavingDate] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [portionMultiplier, setPortionMultiplier] = useState(1)

  // Ref to always access latest editingFoods (avoids stale closure issues)
  const editingFoodsRef = useRef(editingFoods)
  editingFoodsRef.current = editingFoods

  // Sync editingMealType when mealType prop changes (after refetch)
  useEffect(() => {
    if (!isInEditMode) {
      setEditingMealType(normalizedMealType)
    }
  }, [normalizedMealType, isInEditMode])

  // Sync editingLogDate when logDate prop changes (after refetch).
  // Slice to 10 chars defends against API responses that return the legacy
  // datetime form ("YYYY-MM-DDTHH:MM:SS") instead of a bare date — the native
  // <input type="date"> renders empty if the value isn't strictly YYYY-MM-DD.
  useEffect(() => {
    if (!isInEditMode) {
      setEditingLogDate((logDate || todayIso).slice(0, 10))
    }
  }, [logDate, isInEditMode, todayIso])

  // Add food state
  const [showAddFood, setShowAddFood] = useState(false)
  const [newFoodDescription, setNewFoodDescription] = useState('')
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analyzingIndex, setAnalyzingIndex] = useState(null) // Track which food is being re-analyzed
  const [analyzeError, setAnalyzeError] = useState('')

  // Translation state (for non-English users)
  const [translatedNames, setTranslatedNames] = useState({}) // { 'lang:foodId': translatedName }

  // Save as favorite modal state
  const [showSaveAsFavorite, setShowSaveAsFavorite] = useState(false)
  const [showCopyToDate, setShowCopyToDate] = useState(false)

  // Get current language (strip region code for comparison)
  const currentLanguage = contentLanguage(i18n?.language)

  // Auto-translate food names when language is not English
  useEffect(() => {
    const translateFoodNames = async () => {
      // Skip if English or no foods
      if (currentLanguage === 'en' || foods.length === 0) {
        return
      }

      // Get food IDs that need translation
      const foodIds = foods
        .map(f => f.food?.id || f.food_id)
        .filter(id => id && !translatedNames[`${currentLanguage}:${id}`])

      if (foodIds.length === 0) return

      try {
        const response = await nutritionAPI.translateFoodNamesBatch(foodIds, currentLanguage)
        setTranslatedNames(prev => ({
          ...prev,
          ...Object.fromEntries(Object.entries(response.data.translations ?? {})
            .map(([id, name]) => [`${currentLanguage}:${id}`, name]))
        }))
      } catch (error) {
        console.error('Failed to translate food names:', error)
        // Silently fail - show original names
      }
    }

    translateFoodNames()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- translatedNames would cause infinite loop
  }, [foods, currentLanguage]) // Re-run when foods or language changes

  const config = MEAL_CONFIG[normalizedMealType] || MEAL_CONFIG.BREAKFAST

  // VH-123: Preparation note the user emailed in the food-photo body (e.g.
  // "home-made with stevia, no sugar"). Stored verbatim on meals.description.
  // Hide the generic provenance placeholder and blank/whitespace values so only
  // a real, user-authored note is shown.
  const prepNote = (() => {
    const trimmed = (note || '').trim()
    if (!trimmed || trimmed === GENERIC_INTAKE_DESCRIPTION) return null
    return trimmed
  })()

  // Format time
  const formatTime = (datetime) => {
    if (!datetime) return ''
    const date = new Date(datetime)
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date)
  }

  // Get translated or original food name
  const getDisplayName = (food) => {
    const foodId = food.food?.id || food.food_id
    if (currentLanguage !== 'en' && foodId && translatedNames[`${currentLanguage}:${foodId}`]) {
      return translatedNames[`${currentLanguage}:${foodId}`]
    }
    return food.food?.name || food.name || 'Unknown'
  }

  // Get food names for compact display
  const getFoodNames = () => {
    if (foods.length === 0) return t('nutrition.noFoods', 'No foods')
    const names = foods.map(f => getDisplayName(f)).slice(0, 3)
    const remaining = foods.length - 3
    return names.join(', ') + (remaining > 0 ? ` +${remaining}` : '')
  }

  // Initialize edit mode
  const enterEditMode = () => {
    setIsInEditMode(true)
    setEditingFoods(foods.map(f => ({
      id: f.id,
      name: f.food?.name || f.name || '',
      quantity: f.quantity || 100,
      quantity_unit: f.quantity_unit || 'g',
      calories: f.calories_contributed || f.calories || 0,
      protein: f.protein_contributed || 0,
      carbs: f.carbs_contributed || 0,
      fat: f.fat_contributed || 0
    })))
    setEditingMealType(normalizedMealType)
    setEditingLogDate((logDate || todayIso).slice(0, 10))
    setHasChanges(false)
    setPortionMultiplier(1)
    setShowAddFood(false)
    setNewFoodDescription('')
    setAnalyzeError('')
    if (!isExpanded) onToggle()
  }

  // Cancel edit mode
  const cancelEdit = () => {
    setIsInEditMode(false)
    setEditingFoods([])
    setHasChanges(false)
    setPortionMultiplier(1)
    setShowAddFood(false)
    setNewFoodDescription('')
    setAnalyzeError('')
  }

  // Apply portion multiplier to all foods
  const applyPortionMultiplier = (multiplier) => {
    if (multiplier <= 0 || multiplier === portionMultiplier) return

    const factor = multiplier / portionMultiplier
    const updated = editingFoods.map(f => ({
      ...f,
      quantity: parseFloat((f.quantity * factor).toFixed(1)),
      calories: Math.round(f.calories * factor),
      protein: parseFloat((f.protein * factor).toFixed(1)),
      carbs: parseFloat((f.carbs * factor).toFixed(1)),
      fat: parseFloat((f.fat * factor).toFixed(1)),
      changed: true
    }))

    setEditingFoods(updated)
    setPortionMultiplier(multiplier)
    setHasChanges(true)
  }

  // Helper to refresh all nutrition queries after mutations
  const refreshNutritionQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['meals'] })
    queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
    queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
    onMealUpdated()
  }

  // Update mutation for meal
  const updateMealMutation = useMutation({
    mutationFn: (data) => nutritionAPI.updateMeal(mealId, data),
    onSuccess: refreshNutritionQueries
  })

  // Update mutation for meal food
  const updateMealFoodMutation = useMutation({
    mutationFn: ({ id, data }) => nutritionAPI.updateMealFood(id, data),
    onSuccess: refreshNutritionQueries
  })

  // Delete mutation for meal food
  const deleteMealFoodMutation = useMutation({
    mutationFn: (id) => nutritionAPI.deleteMealFood(id),
    onSuccess: refreshNutritionQueries
  })

  // Add mutation for new analyzed food to meal
  const addAnalyzedFoodMutation = useMutation({
    mutationFn: (data) => nutritionAPI.addAnalyzedFoodToMeal(mealId, data),
    onSuccess: refreshNutritionQueries
  })

  // Format an ISO date (YYYY-MM-DD) for display in the toast — locale-aware.
  const formatDateForToast = (iso) => {
    if (!iso) return ''
    // Append T00:00:00 so parsing happens in local time, not UTC midnight (which can shift the day).
    const d = new Date(`${iso}T00:00:00`)
    if (Number.isNaN(d.getTime())) return iso
    try {
      return new Intl.DateTimeFormat(i18n.language || 'en', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }).format(d)
    } catch {
      return iso
    }
  }

  // Save a new log_date silently. Shows an Undo toast on success, an error toast
  // (and reverts the picker) on failure.
  const handleLogDateChange = async (newDate) => {
    if (!newDate || newDate === editingLogDate) return
    if (newDate > todayIso) {
      // Defensive: max attribute should prevent this, but a manual type-in could bypass.
      toast.error(t('nutrition.mealCard.moveError', 'Could not move meal — try again'))
      return
    }
    const oldDate = editingLogDate
    setEditingLogDate(newDate)
    setIsSavingDate(true)
    try {
      await nutritionAPI.updateMeal(mealId, { log_date: newDate })
      // Refresh totals for source + destination days. The keyed-by-date queries
      // are partial-match invalidated by the no-date filter `['meals']`.
      refreshNutritionQueries()
      // Success toast with Undo. 5 s TTL per spec.
      toast(
        (tst) => (
          <div className="flex items-center space-x-3">
            <span>
              {t('nutrition.mealCard.movedToast', 'Moved to {{date}}', {
                date: formatDateForToast(newDate)
              })}
            </span>
            <button
              type="button"
              onClick={async () => {
                toast.dismiss(tst.id)
                try {
                  await nutritionAPI.updateMeal(mealId, { log_date: oldDate })
                  setEditingLogDate(oldDate)
                  refreshNutritionQueries()
                } catch (err) {
                  console.error('[MealCard] Undo move failed:', err)
                  toast.error(
                    t('nutrition.mealCard.moveError', 'Could not move meal — try again')
                  )
                }
              }}
              className="px-2 py-0.5 text-sm font-semibold text-indigo-300 hover:text-indigo-100 underline"
            >
              {t('nutrition.mealCard.movedToastUndo', 'Undo')}
            </button>
          </div>
        ),
        { duration: 5000 }
      )
    } catch (err) {
      console.error('[MealCard] Failed to update meal date:', err)
      setEditingLogDate(oldDate)
      toast.error(t('nutrition.mealCard.moveError', 'Could not move meal — try again'))
    } finally {
      setIsSavingDate(false)
    }
  }

  // Handle food field change
  const handleFoodChange = (index, field, value) => {
    const updated = [...editingFoods]
    updated[index] = { ...updated[index], [field]: value, changed: true }
    setEditingFoods(updated)
    setHasChanges(true)
  }

  // Remove food from edit list
  const handleRemoveFood = (index) => {
    const food = editingFoods[index]
    if (food.id) {
      // Delete from server
      deleteMealFoodMutation.mutate(food.id)
    }
    const updated = editingFoods.filter((_, i) => i !== index)
    setEditingFoods(updated)
    setHasChanges(true)
  }

  // Show add food input
  const handleShowAddFood = () => {
    setShowAddFood(true)
    setNewFoodDescription('')
    setAnalyzeError('')
  }

  // Analyze food description with AI
  const handleAnalyzeFood = async () => {
    if (!newFoodDescription.trim()) return

    setIsAnalyzing(true)
    setAnalyzeError('')

    try {
      const response = await nutritionAPI.analyzeFoodItem(newFoodDescription.trim())
      const result = response.data

      // Add analyzed food to editing list
      setEditingFoods([...editingFoods, {
        id: null,
        name: result.name,
        quantity: result.quantity,
        quantity_unit: result.quantity_unit,
        calories: Math.round(result.calories),
        protein: parseFloat(result.protein.toFixed(1)),
        carbs: parseFloat(result.carbs.toFixed(1)),
        fat: parseFloat(result.fat.toFixed(1)),
        isNew: true,
        confidence: result.confidence
      }])

      setHasChanges(true)
      setShowAddFood(false)
      setNewFoodDescription('')
    } catch (error) {
      console.error('Failed to analyze food:', error)
      setAnalyzeError(error.response?.data?.detail || t('nutrition.edit.analyzeFailed', 'Failed to analyze food'))
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Re-analyze existing food item with current quantity
  const handleReanalyzeFood = async (index) => {
    // Use ref to get latest state (avoids stale closure issues)
    const currentFoods = editingFoodsRef.current
    const food = currentFoods[index]
    if (!food.name) return

    setAnalyzingIndex(index)
    setAnalyzeError('')

    try {
      // Build description from current quantity and name
      const description = `${food.quantity}${food.quantity_unit} ${food.name}`
      console.log('Re-analyzing food with quantity:', food.quantity, food.quantity_unit, '- Full description:', description)
      const response = await nutritionAPI.analyzeFoodItem(description)
      const result = response.data
      console.log('Analysis result:', result)

      // Update the food item with new nutritional values (keep user's edited name)
      const updated = [...currentFoods]
      updated[index] = {
        ...updated[index],
        // Keep the user's edited name - don't replace with AI suggestion
        calories: Math.round(result.calories),
        protein: parseFloat(result.protein.toFixed(1)),
        carbs: parseFloat(result.carbs.toFixed(1)),
        fat: parseFloat(result.fat.toFixed(1)),
        changed: true
      }
      setEditingFoods(updated)
      setHasChanges(true)
    } catch (error) {
      console.error('Failed to re-analyze food:', error)
      setAnalyzeError(error.response?.data?.detail || t('nutrition.edit.analyzeFailed', 'Failed to analyze food'))
    } finally {
      setAnalyzingIndex(null)
    }
  }

  // Add empty food row (manual entry)
  const handleAddEmptyFood = () => {
    setEditingFoods([...editingFoods, {
      id: null,
      name: '',
      quantity: 100,
      quantity_unit: 'g',
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      isNew: true
    }])
    setHasChanges(true)
    setShowAddFood(false)
  }

  // Calculate totals from editing foods
  const calculateEditTotals = () => {
    return editingFoods.reduce((acc, f) => ({
      calories: acc.calories + (parseFloat(f.calories) || 0),
      protein: acc.protein + (parseFloat(f.protein) || 0),
      carbs: acc.carbs + (parseFloat(f.carbs) || 0),
      fat: acc.fat + (parseFloat(f.fat) || 0)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 })
  }

  // Save all changes
  const handleSave = async () => {
    try {
      // Update meal type if changed (API expects lowercase: breakfast, lunch, dinner, snack)
      if (editingMealType !== normalizedMealType) {
        await nutritionAPI.updateMeal(mealId, { meal_type: editingMealType.toLowerCase() })
      }

      // Process each food item (only call API for new or changed foods)
      for (const food of editingFoods) {
        if (food.isNew && food.name) {
          await nutritionAPI.addAnalyzedFoodToMeal(mealId, {
            food_name: food.name,
            quantity: parseFloat(food.quantity),
            quantity_unit: food.quantity_unit,
            calories: parseFloat(food.calories),
            protein: parseFloat(food.protein),
            carbs: parseFloat(food.carbs),
            fat: parseFloat(food.fat)
          })
        } else if (food.id && food.changed) {
          await nutritionAPI.updateMealFood(food.id, {
            food_name: food.name,
            quantity: parseFloat(food.quantity),
            quantity_unit: food.quantity_unit,
            calories_contributed: parseFloat(food.calories),
            protein_contributed: parseFloat(food.protein),
            carbs_contributed: parseFloat(food.carbs),
            fat_contributed: parseFloat(food.fat)
          })
        }
      }

      // Reset local state
      setIsInEditMode(false)
      setEditingFoods([])
      setHasChanges(false)
      setShowAddFood(false)

      // Invalidate all nutrition queries to trigger refetch with fresh data
      queryClient.invalidateQueries({ queryKey: ['meals'] })
      queryClient.invalidateQueries({ queryKey: ['foodLogs'] })
      queryClient.invalidateQueries({ queryKey: ['weeklySummary'] })
      onMealUpdated()
    } catch (error) {
      console.error('[MealSave] Error during save:', error)
      alert(t('nutrition.edit.saveFailed', 'Failed to save changes'))
    }
  }

  const isEditMode = isInEditMode
  const editTotals = isEditMode ? calculateEditTotals() : totals

  return (
    <div className={`bg-surface-container rounded-lg border ${config.borderColor} overflow-hidden transition-all duration-200 shadow-sm hover:shadow-md`}>
      {/* Compact Header - Always visible */}
      <div className={`${config.bgColor} px-4 py-3`}>
        <div className="flex items-center justify-between">
          {/* Left: Icon, Type, Time, Foods */}
          <div className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer" onClick={onToggle}>
            <span className="text-2xl flex-shrink-0">{config.icon}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2">
                <span className={`font-semibold ${config.color}`}>
                  {normalizedMealType.charAt(0) + normalizedMealType.slice(1).toLowerCase()}
                </span>
                <span className="text-gray-400 text-sm">
                  <ClockIcon className="h-3 w-3 inline mr-1" />
                  {formatTime(mealTime)}
                </span>
              </div>
              <p className="text-sm text-gray-600 truncate flex items-center">
                {prepNote && (
                  <DocumentTextIcon
                    className="h-3.5 w-3.5 mr-1 flex-shrink-0 text-indigo-500"
                    title={t('nutrition.mealCard.prepNoteIndicator', 'Has a preparation note')}
                    aria-label={t('nutrition.mealCard.prepNoteIndicator', 'Has a preparation note')}
                  />
                )}
                <span className="truncate">{getFoodNames()}</span>
              </p>
            </div>
          </div>

          {/* Right: Calories + Actions */}
          <div className="flex items-center space-x-3 flex-shrink-0">
            <div className="text-right">
              <span className="text-lg font-bold text-gray-900">{Math.round(totals.calories)}</span>
              <span className="text-xs text-gray-500 ml-1">cal</span>
            </div>

            {/* Save as Favorite Button */}
            <button
              onClick={(e) => { e.stopPropagation(); setShowSaveAsFavorite(true); }}
              className="p-1.5 text-gray-500 hover:text-yellow-500 hover:bg-yellow-50 rounded-lg transition-colors"
              title={t('favorites.saveAsFavorite', 'Save as Favorite')}
            >
              <StarIcon className="h-4 w-4" />
            </button>

            {/* VH-161: Copy to another day */}
            {onCopyMeal && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowCopyToDate(true); }}
                className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title={t('nutrition.copyMealTitle', 'Copy this meal')}
                data-testid="copy-meal-open"
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </button>
            )}

            {/* Edit Button */}
            <button
              onClick={(e) => { e.stopPropagation(); enterEditMode(); }}
              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              title={t('common.edit', 'Edit')}
            >
              <PencilIcon className="h-4 w-4" />
            </button>

            {/* Delete Button */}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(mealId); }}
              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title={t('common.delete', 'Delete')}
            >
              <TrashIcon className="h-4 w-4" />
            </button>

            {/* Expand/Collapse */}
            <button
              onClick={onToggle}
              className="p-1.5 text-gray-400 hover:text-gray-600"
            >
              {isExpanded ? (
                <ChevronUpIcon className="h-5 w-5" />
              ) : (
                <ChevronDownIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content - Edit Mode */}
      {isExpanded && (
        <div className="p-4 space-y-4 border-t border-gray-100">
          {/* VH-123: Preparation note the user emailed with the food photo.
              Read-only context (editing the note in-app is out of scope). */}
          {prepNote && (
            <div className="flex items-start space-x-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2">
              <DocumentTextIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-indigo-500" />
              <div className="min-w-0">
                <p className="text-xs font-medium text-indigo-700">
                  {t('nutrition.mealCard.prepNoteLabel', 'Preparation note')}
                </p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                  {prepNote}
                </p>
              </div>
            </div>
          )}

          {/* Meal date row (only in edit mode) */}
          {isEditMode && (
            <div className="flex items-center space-x-2 flex-wrap gap-y-2">
              <label
                htmlFor={`meal-date-${mealId}`}
                className="text-sm font-medium text-gray-700 flex items-center"
              >
                <CalendarIcon className="h-4 w-4 mr-1 text-gray-500" />
                {t('nutrition.mealCard.dateLabel', 'Date')}:
              </label>
              <input
                id={`meal-date-${mealId}`}
                type="date"
                value={editingLogDate}
                max={todayIso}
                disabled={isSavingDate}
                onChange={(e) => handleLogDateChange(e.target.value)}
                className="form-input text-sm disabled:opacity-50"
                aria-label={t('nutrition.mealCard.dateLabel', 'Date')}
              />
            </div>
          )}

          {/* Meal Type and Portions Selector (only in edit mode) */}
          {isEditMode && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('nutrition.mealType', 'Meal Type')}:
                </label>
                <select
                  value={editingMealType}
                  onChange={(e) => { setEditingMealType(e.target.value); setHasChanges(true); }}
                  className="form-select text-sm"
                >
                  <option value="BREAKFAST">{t('nutrition.mealTypes.breakfast', 'Breakfast')}</option>
                  <option value="LUNCH">{t('nutrition.mealTypes.lunch', 'Lunch')}</option>
                  <option value="DINNER">{t('nutrition.mealTypes.dinner', 'Dinner')}</option>
                  <option value="SNACK">{t('nutrition.mealTypes.snack', 'Snack')}</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700" title={t('nutrition.edit.portionsHint', 'Multiply all quantities and nutrition by this factor')}>
                  {t('nutrition.edit.portions', 'Portions')}:
                </label>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4].map(num => (
                    <button
                      key={num}
                      onClick={() => applyPortionMultiplier(num)}
                      className={`w-8 h-8 text-sm rounded-md transition-colors ${
                        portionMultiplier === num
                          ? 'bg-indigo-600 text-on-neutral'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {num}x
                    </button>
                  ))}
                  <input
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.5"
                    value={portionMultiplier}
                    onChange={(e) => applyPortionMultiplier(parseFloat(e.target.value) || 1)}
                    className="w-14 px-2 py-1 text-sm text-center"
                    title={t('nutrition.edit.customPortions', 'Custom portion multiplier')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Foods Card Stack */}
          <div className="space-y-2">
            {(isEditMode ? editingFoods : foods).map((food, index) => (
              <NutritionFoodCard
                key={food.id || `new-${index}`}
                food={food}
                displayName={isEditMode ? food.name : getDisplayName(food)}
                editable={isEditMode}
                isReanalyzing={analyzingIndex === index}
                onQuantityChange={(value) => handleFoodChange(index, 'quantity', value)}
                onCaloriesChange={(value) => handleFoodChange(index, 'calories', value)}
                onProteinChange={(value) => handleFoodChange(index, 'protein', value)}
                onCarbsChange={(value) => handleFoodChange(index, 'carbs', value)}
                onFatChange={(value) => handleFoodChange(index, 'fat', value)}
                onDelete={() => handleRemoveFood(index)}
                onReanalyze={isEditMode && food.name ? () => handleReanalyzeFood(index) : undefined}
              />
            ))}

            {/* Totals Footer */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <span className="text-sm font-medium text-gray-700">
                {t('nutrition.total', 'Total')}
              </span>
              <MacroBadge
                calories={editTotals.calories}
                protein={editTotals.protein}
                carbs={editTotals.carbs}
                fat={editTotals.fat}
              />
            </div>
          </div>

          {/* Add Food Section (only in edit mode) */}
          {isEditMode && (
            <div className="space-y-2">
              {showAddFood ? (
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newFoodDescription}
                      onChange={(e) => setNewFoodDescription(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAnalyzeFood()}
                      placeholder={t('nutrition.edit.foodDescriptionPlaceholder', 'e.g., "20g dry fruits" or "1 banana"')}
                      className="flex-1 px-3 py-2 text-sm"
                      autoFocus
                      disabled={isAnalyzing}
                    />
                    <button
                      onClick={handleAnalyzeFood}
                      disabled={isAnalyzing || !newFoodDescription.trim()}
                      className="px-3 py-2 text-sm text-on-neutral bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 flex items-center"
                    >
                      {isAnalyzing ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-1" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {t('common.loading', 'Loading...')}
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="h-4 w-4 mr-1" />
                          {t('nutrition.edit.analyzeWithAI', 'Analyze')}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => { setShowAddFood(false); setNewFoodDescription(''); setAnalyzeError(''); }}
                      className="p-2 text-gray-500 hover:text-gray-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {analyzeError && (
                    <p className="text-sm text-red-600">{analyzeError}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    {t('nutrition.edit.aiHint', 'AI will analyze the description and fill in nutritional values')}
                  </p>
                  <button
                    onClick={handleAddEmptyFood}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    {t('nutrition.edit.addManualEntry', 'Or add manually without AI')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleShowAddFood}
                  className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {t('nutrition.addFood', 'Add Food')}
                </button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100">
            {isEditMode ? (
              <>
                <button
                  onClick={cancelEdit}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-4 w-4 inline mr-1" />
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || updateMealMutation.isPending || updateMealFoodMutation.isPending || addAnalyzedFoodMutation.isPending}
                  className="px-3 py-1.5 text-sm text-on-neutral bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4 inline mr-1" />
                  {t('common.save', 'Save')}
                </button>
              </>
            ) : (
              <button
                onClick={enterEditMode}
                className="px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
              >
                <PencilIcon className="h-4 w-4 inline mr-1" />
                {t('nutrition.editFoods', 'Edit Foods')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Save as Favorite Modal */}
      <SaveAsFavoriteModal
        isOpen={showSaveAsFavorite}
        mealId={mealId}
        mealType={normalizedMealType}
        defaultName={getFoodNames()}
        onSave={() => {
          setShowSaveAsFavorite(false)
        }}
        onClose={() => setShowSaveAsFavorite(false)}
      />

      {/* VH-161: Copy this meal onto another day */}
      {showCopyToDate && onCopyMeal && (
        <CopyMealToDateDialog
          mealType={normalizedMealType}
          sourceDate={logDate}
          onClose={() => setShowCopyToDate(false)}
          onCopy={(targetDate) => onCopyMeal({ mealId, targetDate })}
        />
      )}
    </div>
  )
}
